/**
 * CreateRoute.jsx
 * Componente para criar rotas no mapa
 *
 * Permite selecionar origem e destino clicando no mapa ou buscando endereco
 * Calcula a rota usando OSRM e retorna a geometria para o componente pai
 */

import {
  useState, useEffect, useCallback, useId, useRef,
} from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  InputAdornment,
  Chip,
  Button,
} from '@mui/material';
import {
  MyLocation as OriginIcon,
  Flag as DestinationIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Route as RouteIcon,
  SwapVert as SwapIcon,
  GpsFixed as MapSelectIcon,
} from '@mui/icons-material';
import maplibregl from 'maplibre-gl';

import MapView, { map } from '../../map/core/MapView';
import MapCurrentLocation from '../../map/MapCurrentLocation';
import MapMarkers from '../../map/MapMarkers';
import { useTranslation } from './LocalizationProvider';
import { usePremiumTokens } from '../theme/usePremiumTokens';
import {
  calculateRoute,
  searchAddress,
  reverseGeocode,
  formatDistance,
  formatDuration,
} from '../services/routingService';


const CreateRoute = ({
  value,
  onChange,
  origin: initialOrigin,
  destination: initialDestination,
  originLabel,
  destinationLabel,
  showMap = true,
  mapHeight = 400,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const mapId = useId();

  const [origin, setOrigin] = useState(value?.origin || initialOrigin || null);
  const [destination, setDestination] = useState(value?.destination || initialDestination || null);
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchMode, setSearchMode] = useState(null); // 'origin' | 'destination'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Refs para evitar loop infinito - armazena callbacks/valores sem triggerar re-render
  const onChangeRef = useRef(onChange);
  const tokensRef = useRef(tokens);
  useEffect(() => {
    onChangeRef.current = onChange;
    tokensRef.current = tokens;
  }, [onChange, tokens]);

  // Buscar endereco
  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const results = await searchAddress(query, { limit: 5 });
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounce da busca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        handleSearch(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  // Selecionar resultado da busca
  const handleSelectResult = async (result) => {
    const point = {
      lat: result.lat,
      lng: result.lng,
      address: result.displayName,
    };

    if (searchMode === 'origin') {
      setOrigin(point);
    } else {
      setDestination(point);
    }

    setSearchQuery('');
    setSearchResults([]);
    setSearchMode(null);
  };

  // Clique no mapa
  const handleMapClick = useCallback(async (e) => {
    if (!searchMode) return;

    const { lng, lat } = e.lngLat;
    setLoading(true);

    try {
      const result = await reverseGeocode(lat, lng);
      const point = {
        lat,
        lng,
        address: result.displayName,
      };

      if (searchMode === 'origin') {
        setOrigin(point);
      } else {
        setDestination(point);
      }
    } catch {
      // Se falhar o geocoding, usa só as coordenadas
      const point = {
        lat,
        lng,
        address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      };

      if (searchMode === 'origin') {
        setOrigin(point);
      } else {
        setDestination(point);
      }
    } finally {
      setLoading(false);
      setSearchMode(null);
    }
  }, [searchMode]);

  // Adicionar listener de clique no mapa
  useEffect(() => {
    if (!map || !showMap) return;

    map.on('click', handleMapClick);
    return () => map.off('click', handleMapClick);
  }, [handleMapClick, showMap]);

  // Ajustar zoom para mostrar origem/destino
  useEffect(() => {
    if (!map || !showMap) return;

    if (origin && destination) {
      const bounds = new maplibregl.LngLatBounds()
        .extend([origin.lng, origin.lat])
        .extend([destination.lng, destination.lat]);
      map.fitBounds(bounds, { padding: 50 });
    } else if (origin) {
      map.flyTo({ center: [origin.lng, origin.lat], zoom: 14 });
    } else if (destination) {
      map.flyTo({ center: [destination.lng, destination.lat], zoom: 14 });
    }
  }, [origin, destination, showMap]);

  // Criar markers para MapMarkers (mesmo padrao do TripReport)
  const routeMarkers = [];
  if (origin) {
    routeMarkers.push({
      latitude: origin.lat,
      longitude: origin.lng,
      image: 'start-success',
    });
  }
  if (destination) {
    routeMarkers.push({
      latitude: destination.lat,
      longitude: destination.lng,
      image: 'finish-error',
    });
  }

  // Calcular rota quando tiver origem e destino
  useEffect(() => {
    if (!origin || !destination) {
      setRoute(null);
      return;
    }

    const calcRoute = async () => {
      setLoading(true);
      try {
        const result = await calculateRoute([
          { lat: origin.lat, lng: origin.lng },
          { lat: destination.lat, lng: destination.lng },
        ], 'car', { geometries: 'geojson' });

        setRoute(result);

        // Notificar componente pai usando ref para evitar loop infinito
        // Suporta dois formatos: onChange(origin, destination) ou onChange({ origin, destination, route, area })
        if (onChangeRef.current) {
          const routeData = {
            origin,
            destination,
            route: result,
            area: `LINESTRING (${result.geometry.coordinates.map((c) => `${c[1]} ${c[0]}`).join(', ')})`,
          };

          // Tenta detectar qual formato o onChange espera
          try {
            onChangeRef.current(origin, destination, routeData);
          } catch {
            onChangeRef.current(routeData);
          }
        }
      } catch (error) {
        console.error('Route calculation error:', error);
        setRoute(null);
      } finally {
        setLoading(false);
      }
    };

    calcRoute();
  }, [origin, destination]);

  // Desenhar rota no mapa
  useEffect(() => {
    if (!map || !showMap) return;

    const sourceId = `route-${mapId}`;
    const layerId = `route-line-${mapId}`;

    // Remover layer/source anterior
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }

    // Adicionar rota se existir (usa tokensRef para evitar loop infinito)
    if (route?.geometry) {
      map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: route.geometry,
        },
      });

      map.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': tokensRef.current?.accent?.primary || '#00d4aa',
          'line-width': 4,
          'line-opacity': 0.8,
        },
      });
    }

    return () => {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
    };
  }, [route, showMap, mapId]);

  // Trocar origem e destino
  const handleSwap = () => {
    const temp = origin;
    setOrigin(destination);
    setDestination(temp);
  };

  // Limpar ponto
  const handleClear = (type) => {
    if (type === 'origin') {
      setOrigin(null);
    } else {
      setDestination(null);
    }
    setRoute(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Controles de origem/destino */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: tokens.surfaceAlt,
          border: `1px solid ${tokens.border}`,
        }}
      >
        {/* Origem */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <OriginIcon sx={{ color: tokens.success }} />
          <TextField
            fullWidth
            size="small"
            placeholder={originLabel || t('routeOrigin') || 'Origem'}
            value={searchMode === 'origin' ? searchQuery : (origin?.address || '')}
            onChange={(e) => {
              setSearchMode('origin');
              setSearchQuery(e.target.value);
            }}
            onFocus={() => {
              setSearchMode('origin');
              setSearchQuery(origin?.address || '');
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {origin && (
                    <IconButton size="small" onClick={() => handleClear('origin')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  {searching && searchMode === 'origin' && (
                    <CircularProgress size={20} />
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: tokens.surface,
              },
            }}
          />
          <IconButton
            size="small"
            onClick={() => setSearchMode(searchMode === 'origin' ? null : 'origin')}
            sx={{
              bgcolor: searchMode === 'origin' ? tokens.success : tokens.surface,
              color: searchMode === 'origin' ? tokens.surface : tokens.textSecondary,
              '&:hover': {
                bgcolor: searchMode === 'origin' ? tokens.success : tokens.surfaceAlt,
              },
            }}
            title={t('sharedClickOnMap') || 'Clique no mapa'}
          >
            <MapSelectIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Botao de swap */}
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
          <IconButton
            size="small"
            onClick={handleSwap}
            disabled={!origin || !destination}
            sx={{
              bgcolor: tokens.surface,
              '&:hover': { bgcolor: tokens.surfaceAlt },
            }}
          >
            <SwapIcon />
          </IconButton>
        </Box>

        {/* Destino */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DestinationIcon sx={{ color: tokens.error }} />
          <TextField
            fullWidth
            size="small"
            placeholder={destinationLabel || t('routeDestination') || 'Destino'}
            value={searchMode === 'destination' ? searchQuery : (destination?.address || '')}
            onChange={(e) => {
              setSearchMode('destination');
              setSearchQuery(e.target.value);
            }}
            onFocus={() => {
              setSearchMode('destination');
              setSearchQuery(destination?.address || '');
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {destination && (
                    <IconButton size="small" onClick={() => handleClear('destination')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  {searching && searchMode === 'destination' && (
                    <CircularProgress size={20} />
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: tokens.surface,
              },
            }}
          />
          <IconButton
            size="small"
            onClick={() => setSearchMode(searchMode === 'destination' ? null : 'destination')}
            sx={{
              bgcolor: searchMode === 'destination' ? tokens.error : tokens.surface,
              color: searchMode === 'destination' ? tokens.surface : tokens.textSecondary,
              '&:hover': {
                bgcolor: searchMode === 'destination' ? tokens.error : tokens.surfaceAlt,
              },
            }}
            title={t('sharedClickOnMap') || 'Clique no mapa'}
          >
            <MapSelectIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Resultados da busca */}
        {searchResults.length > 0 && searchMode && (
          <Paper
            elevation={3}
            sx={{
              mt: 1,
              maxHeight: 200,
              overflow: 'auto',
              borderRadius: 1,
            }}
          >
            <List dense>
              {searchResults.map((result) => (
                <ListItemButton
                  key={result.id}
                  onClick={() => handleSelectResult(result)}
                >
                  <ListItemIcon>
                    <SearchIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={result.displayName}
                    primaryTypographyProps={{
                      variant: 'body2',
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}

        {/* Info do modo de selecao */}
        {searchMode && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size="small"
              color={searchMode === 'origin' ? 'success' : 'error'}
              label={searchMode === 'origin' ? 'Clique no mapa para origem' : 'Clique no mapa para destino'}
            />
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setSearchMode(null);
                setSearchQuery('');
                setSearchResults([]);
              }}
            >
              Cancelar
            </Button>
          </Box>
        )}

        {/* Info da rota */}
        {route && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: tokens.surface,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <RouteIcon sx={{ color: tokens.primary }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {formatDistance(route.distance)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {formatDuration(route.duration)}
              </Typography>
            </Box>
          </Box>
        )}

        {loading && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Paper>

      {/* Mapa */}
      {showMap && (
        <Paper
          elevation={0}
          sx={{
            height: mapHeight,
            borderRadius: 2,
            overflow: 'hidden',
            border: `1px solid ${tokens.border}`,
            position: 'relative',
          }}
        >
          <MapView>
            <MapCurrentLocation />
            {routeMarkers.length > 0 && <MapMarkers markers={routeMarkers} />}
          </MapView>
        </Paper>
      )}
    </Box>
  );
};

export default CreateRoute;
